import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_INVENTORY_API ?? '/api/v1';

export type Quantity = {
  on_hand: number;
  available: number;
  reserved: number;
  allocated?: number;
  in_transit?: number;
  damaged?: number;
};

export type Warehouse = {
  id: string;
  tenant_id: string;
  organization_id: string;
  code: string;
  name: string;
  description?: string;
  address?: string;
  status: string;
  is_default?: boolean;
  metadata?: Record<string, string>;
  created_at: string;
  updated_at: string;
};

export type InventoryBalance = {
  id: string;
  product_id: string;
  warehouse_id: string;
  quantity: Quantity;
  status: string;
  updated_at: string;
};

export type StockReservation = {
  id: string;
  product_id: string;
  warehouse_id: string;
  order_id?: string;
  quantity: number;
  status: string;
  expires_at?: string;
  created_at: string;
};

export type TransferItem = {
  id?: string;
  product_id: string;
  quantity: number;
};

export type TransferOrder = {
  id: string;
  from_warehouse_id: string;
  to_warehouse_id: string;
  status: string;
  items: TransferItem[];
  created_at: string;
  updated_at: string;
};

export type InventoryAdjustment = {
  id: string;
  product_id: string;
  warehouse_id: string;
  quantity_change: number;
  quantity_after: number;
  reason?: string;
  created_at: string;
};

export type CycleCount = {
  id: string;
  warehouse_id: string;
  status: string;
  scheduled_at?: string;
  completed_at?: string;
  variance_count?: number;
  created_at: string;
};

export type Forecast = {
  product_id: string;
  warehouse_id: string;
  horizon_days: number;
  predicted_demand: number;
  reorder_point: number;
  suggested_qty: number;
  confidence: number;
  generated_at: string;
};

export type InventoryHealth = {
  score: number;
  stockout_risk: number;
  overstock_risk: number;
  low_stock_count: number;
  out_of_stock_count: number;
  total_skus?: number;
  generated_at: string;
};

export type InventoryDashboard = {
  total_warehouses: number;
  total_skus: number;
  total_on_hand: number;
  total_reserved: number;
  active_transfers: number;
  pending_reservations: number;
  health_score: number;
  stats?: Record<string, unknown>;
  generated_at: string;
};

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function inventoryFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!res.ok) throw new Error(`Inventory ${path} failed: ${res.status} ${await res.text()}`);
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const inventoryApi = {
  dashboard: () => inventoryFetch<InventoryDashboard>('/inventory/dashboard'),

  listWarehouses: (params?: { status?: string; q?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return inventoryFetch<Warehouse[]>(`/warehouses${q ? `?${q}` : ''}`);
  },
  createWarehouse: (input: { code: string; name: string; description?: string; address?: string }) =>
    inventoryFetch<Warehouse>('/warehouses', { method: 'POST', body: JSON.stringify(input) }),
  updateWarehouse: (id: string, input: Partial<Warehouse>) =>
    inventoryFetch<Warehouse>(`/warehouses/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  deleteWarehouse: (id: string) => inventoryFetch<void>(`/warehouses/${id}`, { method: 'DELETE' }),

  listInventory: (params?: { product_id?: string; warehouse_id?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return inventoryFetch<InventoryBalance[]>(`/inventory${q ? `?${q}` : ''}`);
  },
  getInventoryByProduct: (productId: string, warehouseId?: string) => {
    const q = warehouseId ? `?warehouse_id=${encodeURIComponent(warehouseId)}` : '';
    return inventoryFetch<InventoryBalance[]>(`/inventory/${productId}${q}`);
  },
  adjustInventory: (input: { product_id: string; warehouse_id: string; quantity: number; reason?: string; reference?: string }) =>
    inventoryFetch<InventoryAdjustment>('/inventory/adjust', { method: 'POST', body: JSON.stringify(input) }),
  transferInventory: (input: { from_warehouse_id: string; to_warehouse_id: string; items: TransferItem[] }) =>
    inventoryFetch<TransferOrder>('/inventory/transfer', { method: 'POST', body: JSON.stringify(input) }),
  reserveInventory: (input: { product_id: string; warehouse_id: string; quantity: number; order_id?: string }) =>
    inventoryFetch<StockReservation>('/inventory/reserve', { method: 'POST', body: JSON.stringify(input) }),
  releaseInventory: (reservationId: string) =>
    inventoryFetch<StockReservation>('/inventory/release', { method: 'POST', body: JSON.stringify({ reservation_id: reservationId }) }),

  listTransfers: () => inventoryFetch<TransferOrder[]>('/transfers'),
  createTransfer: (input: { from_warehouse_id: string; to_warehouse_id: string; items: TransferItem[] }) =>
    inventoryFetch<TransferOrder>('/transfers', { method: 'POST', body: JSON.stringify(input) }),

  createCycleCount: (input: { warehouse_id: string }) =>
    inventoryFetch<CycleCount>('/cycle-counts', { method: 'POST', body: JSON.stringify(input) }),

  forecast: (params: { product_id: string; warehouse_id: string; horizon_days?: string }) => {
    const q = new URLSearchParams(params).toString();
    return inventoryFetch<Forecast>(`/inventory/forecast?${q}`);
  },
  health: () => inventoryFetch<InventoryHealth>('/inventory/health'),
};
